import {
  OutlinedInput,
  InputAdornment,
  Stack,
  Button,
  useTheme,
  Skeleton,
  Typography,
  IconButton,
  Grid,
  Tooltip,
  FormControlLabel,
  Switch,
  CircularProgress,
} from "@mui/material";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../../Components/Misc/ListPageHeader";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import { useState, useEffect } from "react";
import { setupService } from "../../../../Core/Api/services";
import useCustomSnackbar from "../../../../Utils/Hooks/useCustomSnackbar";
import { DbAgentInfos } from "../../../../Core/Api/Types/db-agent-infos";
import FormSection from "../../../../Components/Forms/FormSection/form-section";
import FormInputText from "../../../../Components/Forms/FormInputText/form-input-text";
import FormAutocomplete from "../../../../Components/Forms/FormAutocomplete/form-autocomplete";
import { JobTypes } from "../../../../Core/Api/Types/job-types";
import FormInputNumber from "../../../../Components/Forms/FormInputNumber/form-input-number";
import { PlatformInfos } from "../../../../Core/Api/Types/platform-infos";
import { produce } from "immer";
import { DbAgentLicenseResourceInfos } from "../../../../Core/Api/Types/db-agent-license-resource-infos";
import CloseIcon from "@mui/icons-material/Close";
import { ConfigCard } from "../../../../Components/Modals/ModalCreateMetadata/rating-desc-values-form";
import { AgentType } from "../../../../Core/Api/Types/agent-type";
import { AgentStatus } from "../../../../Core/Api/Types/agent-status";
import { AgentDispatchMode } from "../../../../Core/Api/Types/agent-dispatch-mode";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import SaveIcon from "@mui/icons-material/Save";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { DbAgentGrpcSettingsInfos } from "../../../../Core/Api/Types/db-agent-grpc-settings-infos";
import { useDispatch } from "react-redux";
import ErrorIcon from "@mui/icons-material/Error";
import { SlotDefinition } from "../../../../Core/Api/Types/slot-definition";
import { useTranslation } from "react-i18next";

const formId = "agents-and-licences";

function AdminSettingsAgentsAndLicenses() {
  const { t } = useTranslation();
  const enqueueCustomSnackbar = useCustomSnackbar();
  const theme = useTheme();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(true);
  const [isSavingIndexes, setIsSavingIndexes] = useState<number[]>([]);

  const [mainOpen, setMainOpen] = useState<boolean>(false);
  const [otherOpen, setOtherOpen] = useState<boolean>(false);
  const [grpsOpen, setGrpsOpen] = useState<boolean>(false);

  const [filterText, setFilterText] = useState("");
  const [agents, setAgents] = useState<DbAgentInfos[]>([]);
  const [platforms, setPlatforms] = useState<PlatformInfos[]>([]);
  const [selectedAgentIndex, setSelectedAgentIndex] = useState<number>(-1);
  const [selectedSlotIndex, setSelectedSlotIndex] = useState<number>(-1);
  const [selectedPlatforms, setSelectedPlatforms] = useState<number[]>([]);

  const [errorsMap, setErrorsMap] = useState<any>({});

  const [slotToCopy, setSlotToCopy] = useState<SlotDefinition[] | null>(null);

  const [editedAgentIndexes, setEditedAgentIndexes] = useState<number[]>([]);

  const [filterAgents, setFilterAgents] = useState<string>("");

  useEffect(() => {
    Promise.all([setupService.ListAgents(), setupService.ListPlatforms()]).then(([agentsRes, platformRes]) => {
      if (agentsRes.success && agentsRes.agents) {
        setAgents(agentsRes.agents);
      }
      if (platformRes.success && platformRes.platforms) {
        setPlatforms(platformRes.platforms);
      }
      setIsLoading(false);
    });
  }, []);

  useEffect(() => {
    if (selectedAgentIndex > -1 && !editedAgentIndexes.includes(selectedAgentIndex)) {
      setEditedAgentIndexes((x) => [...x, selectedAgentIndex]);
    }
  }, [agents]);

  const areSlotsEquals = (slot1: SlotDefinition[], slot2: SlotDefinition[]) => {
    if (slot1.length !== slot2.length) return false;
    for (var i = 0; i < slot1.length; i++) {
      if (slot1[i].jobType !== slot2[i].jobType || slot1[i].slotNumbers !== slot2[i].slotNumbers) return false;
    }
    return true;
  };

  const handleSaveIndex = (index: number) => {
    setupService
      .UpdateAgent(agents[index])
      .then((res) => {
        if (res.success) {
          enqueueCustomSnackbar(t("alert:success.agentUpdated"), "success");
          setEditedAgentIndexes((x) => x.filter((i) => i !== index));
          setErrorsMap((x) => ({ ...x, [index]: null }));
        } else {
          enqueueCustomSnackbar(res.message ?? t("alert:error.errorWhileUpdatingAgent"), "error");
        }
        setIsSavingIndexes((x) => x.filter((i) => i !== index));
      })
      .catch((err) => {
        const res = err.response.data;
        setErrorsMap((x) => ({ ...x, [index]: { ...(res as any).errors } }));
        enqueueCustomSnackbar(res.title ?? t("alert:error.errorWhileUpdatingAgent"), "error");
        setIsSavingIndexes((x) => x.filter((i) => i !== index));
      });
  };

  const handleSaveAll = () => {
    setIsSavingIndexes((x) => [...x, ...editedAgentIndexes]);
    editedAgentIndexes.forEach((i) => handleSaveIndex(i));
  };

  const handleSaveCurrent = () => {
    handleSaveIndex(selectedAgentIndex);
    setIsSavingIndexes((x) => [...x, selectedAgentIndex]);
  };

  const handleDelete = () => {
    setSelectedAgentIndex(-1);
    setAgents((x) => x.map((a, i) => (i === selectedAgentIndex ? ({ ...a, deleted: true } as DbAgentInfos) : a)));
    setIsSavingIndexes((x) => x.filter((a, i) => i !== selectedAgentIndex));
    setErrorsMap((x) => ({ ...x, [selectedAgentIndex]: null }));
    setEditedAgentIndexes((x) => x.filter((a) => a !== selectedAgentIndex));
  };

  return (
    <>
      <ListPageHeader mb={3}>
        <ListPageHeaderLeft title={t("component:listPageHeaderLeft.title.agentsAndLicences")} />
        <ListPageHeaderRight>
          <Button
            disabled
            onClick={() => {
              setSelectedAgentIndex(agents.length);
              setAgents(
                produce((draft) => {
                  if (draft)
                    draft.push({
                      $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAgentInfos, VDM.AirLab.Sdk.Core",
                      description: "",
                      hostName: "",
                      hostNameExt: "",
                      isNew: true,
                      id: -500,
                      grpcSettings: new DbAgentGrpcSettingsInfos(),
                      dispatchMode: null,
                      dispatchPriority: 50,
                      isShared: false,
                      k8s: false,
                      licenseSlots: [],
                      platformIds: [],
                      serverInfo: null,
                      status: null,
                      type: null,
                      updateInfos: null,
                    });
                })
              );
            }}
            color="primary"
            variant="contained"
            fullWidth
            endIcon={<AddIcon />}
          >
            {t("button:createNewAgent")}
          </Button>
        </ListPageHeaderRight>
      </ListPageHeader>
      <Stack
        border={`1px solid ${theme.palette.background.paper}`}
        p={0}
        borderRadius={"6px"}
        height={"calc(100vh - 218px)"}
        width={"100%"}
        display={"flex"}
        direction={"row"}
      >
        <Stack
          spacing={1}
          sx={{
            background: theme.palette.background.default,
            p: 1,
            maxHeight: "100%",
          }}
          direction={"column"}
          flex={1}
        >
          {!isLoading && (
            <OutlinedInput
              sx={{ marginTop: "8px !important" }}
              value={filterAgents}
              onChange={(e) => setFilterAgents(e.target.value.toLocaleLowerCase())}
              placeholder={t("field:placeholder.searchIntoAgents")}
              fullWidth
              endAdornment={
                <InputAdornment position="end">
                  <SearchIcon />
                </InputAdornment>
              }
            />
          )}
          <Stack
            spacing={1}
            sx={{
              background: theme.palette.background.default,
              pr: 1,
              overflowY: "scroll",
              maxHeight: "100%",
              ...theme.helpers(theme).scrollBar,
            }}
            direction={"column"}
          >
            {isLoading ? (
              <Stack>
                <Skeleton />
                <Skeleton />
                <Skeleton />
              </Stack>
            ) : (
              agents.map(
                (x, i) =>
                  !x.deleted &&
                  (filterAgents === "" ||
                    x.description?.toLocaleLowerCase().includes(filterAgents) ||
                    x.hostName?.toLocaleLowerCase().includes(filterAgents) ||
                    x.hostNameExt?.toLocaleLowerCase().includes(filterAgents)) && (
                    <ConfigCard
                      errors={errorsMap[`${i}`]}
                      hasData={x.licenseSlots !== null && x.licenseSlots.length > 0}
                      onClick={() => {
                        setSelectedAgentIndex(i);
                      }}
                      edited={editedAgentIndexes.includes(i)}
                      onClickSave={() => {
                        setIsSavingIndexes((x) => [...x, i]);
                        handleSaveIndex(i);
                      }}
                      isSaving={isSavingIndexes.includes(i)}
                      selected={selectedAgentIndex === i}
                      text={x.description ?? ""}
                      textWidth={"10vw"}
                    />
                  )
              )
            )}
          </Stack>
        </Stack>
        <Stack
          sx={{
            background: theme.palette.background.default,
            p: 1,
            maxHeight: "100%",
          }}
          spacing={1}
          direction={"column"}
          flex={1}
        >
          {agents[selectedAgentIndex] && !agents[selectedAgentIndex].deleted && (
            <FormAutocomplete
              placeholder={t("field:placeholder.selectPlatforms")}
              fullWidth
              onChange={(v) => {
                setSelectedPlatforms(v);
              }}
              validationProps={{
                fieldId: "add-platforms",
                formId: formId,
              }}
              getOptionLabel={(option) => platforms.find((x) => x.id === option)?.name ?? ""}
              options={platforms
                .filter((x) => !agents[selectedAgentIndex].licenseSlots?.find((l) => l.platformId === x.id))
                .map((x) => x.id)}
              value={selectedPlatforms}
              multiple
            />
          )}
          {agents[selectedAgentIndex] && !agents[selectedAgentIndex].deleted && (
            <Button
              onClick={() => {
                setSelectedPlatforms([]);
                setAgents(
                  produce((draft) => {
                    const selectedAgent = draft[selectedAgentIndex];
                    if (!selectedAgent || !selectedPlatforms) return;
                    const newLicenceSlots = selectedPlatforms.map((selectedPlatform) => ({
                      $type: "VDM.AirLab.Sdk.Core.Database.Types.DbAgentLicenseResourceInfos, VDM.AirLab.Sdk.Core",
                      platformId: selectedPlatform,
                      slots: [],
                    }));
                    selectedAgent.licenseSlots = selectedAgent.licenseSlots
                      ? [...selectedAgent.licenseSlots, ...newLicenceSlots]
                      : [...newLicenceSlots];
                  })
                );
              }}
              color="primary"
              fullWidth
              endIcon={<AddIcon />}
              disabled={selectedPlatforms.length === 0}
            >
              {t("button:addPlatformLicence")}
            </Button>
          )}
          <Stack
            sx={{
              background: theme.palette.background.default,
              pr: 1,
              maxHeight: "100%",
              overflowY: "scroll",
              ...theme.helpers(theme).scrollBar,
            }}
            spacing={1}
            direction={"column"}
          >
            {agents[selectedAgentIndex] &&
              !agents[selectedAgentIndex].deleted &&
              agents[selectedAgentIndex].licenseSlots &&
              agents[selectedAgentIndex].licenseSlots!.map((x, i) => (
                <ConfigCard
                  hasData={x.slots !== null && x.slots.length > 0}
                  onClick={() => {
                    setSelectedSlotIndex(i);
                  }}
                  inClipboard={slotToCopy !== null && areSlotsEquals(slotToCopy, x.slots ?? [])}
                  onClickCopy={() => {
                    setSlotToCopy(x.slots);
                  }}
                  onClickPaste={
                    slotToCopy !== null
                      ? () => {
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (!currentAgent || !currentAgent.licenseSlots) return;

                              currentAgent.licenseSlots[i] = {
                                ...currentAgent.licenseSlots[i],
                                slots: [...slotToCopy],
                              };
                            })
                          );
                        }
                      : null
                  }
                  onClickClean={() => {
                    setSlotToCopy(null);
                  }}
                  selected={selectedSlotIndex === i}
                  text={platforms.find((p) => p.id === x.platformId)?.name ?? "Unknown"}
                  textWidth={"9.5vw"}
                />
              ))}
          </Stack>
        </Stack>
        <Stack
          spacing={1}
          sx={{
            background: theme.palette.background.default,
            p: 1,
            maxHeight: "100%",
          }}
          mt={1}
          direction={"column"}
          flex={3}
        >
          {selectedAgentIndex > -1 && agents[selectedAgentIndex] && !agents[selectedAgentIndex].deleted && (
            <Stack display={"flex"} spacing={1} direction={"row"} width={"100%"}>
              <Button disabled color="error" onClick={handleDelete} sx={{ flex: 1 }} endIcon={<DeleteOutlineIcon />}>
                {t("button:deleteAgent")}
              </Button>
              <Button
                color="primary"
                disabled={!editedAgentIndexes.includes(selectedAgentIndex)}
                sx={{ flex: 1 }}
                endIcon={
                  isSavingIndexes.includes(selectedAgentIndex) ? (
                    <CircularProgress size={20} thickness={5} color="secondary" />
                  ) : (
                    <SaveIcon />
                  )
                }
                onClick={handleSaveCurrent}
              >
                {t("button:saveCurrent")}
              </Button>
              <Button
                disabled={editedAgentIndexes.length === 0}
                color="primary"
                variant="contained"
                sx={{ flex: 1 }}
                endIcon={
                  isSavingIndexes.length > 0 ? (
                    <CircularProgress size={20} thickness={5} color="secondary" />
                  ) : (
                    <ArrowForwardIcon />
                  )
                }
                onClick={handleSaveAll}
              >
                {t("button:saveAll")}
              </Button>
            </Stack>
          )}
          <Stack
            spacing={1}
            sx={{
              background: theme.palette.background.default,
              p: 1,
              maxHeight: "100%",
              overflowY: "scroll",
              ...theme.helpers(theme).scrollBar,
            }}
            direction={"column"}
          >
            {agents[selectedAgentIndex] && !agents[selectedAgentIndex].deleted && (
              <FormSection
                expanded={mainOpen}
                onExpand={() => setMainOpen((x) => !x)}
                size="small"
                title={t("form:section.title.mainInfos")}
              >
                <Grid spacing={1} container>
                  <Grid item md={6} sm={12} xs={12}>
                    <FormInputText
                      label={t("common:description")}
                      validationProps={{ formId: formId, fieldId: "description" }}
                      value={agents[selectedAgentIndex].description}
                      sx={{ mb: 1 }}
                      onChange={(e) =>
                        setAgents(
                          produce((draft) => {
                            const currentAgent = draft[selectedAgentIndex];
                            if (currentAgent) currentAgent.description = e.target.value;
                          })
                        )
                      }
                    />
                    <FormInputText
                      label={t("field:label.hostNameExt")}
                      validationProps={{ formId: formId, fieldId: "hostNameExt" }}
                      value={agents[selectedAgentIndex].hostNameExt}
                      sx={{ mb: 1 }}
                      onChange={(e) =>
                        setAgents(
                          produce((draft) => {
                            const currentAgent = draft[selectedAgentIndex];
                            if (currentAgent) currentAgent.hostNameExt = e.target.value;
                          })
                        )
                      }
                    />
                  </Grid>
                  <Grid item md={6} sm={12} xs={12}>
                    <FormInputText
                      label={t("field:label.hostName")}
                      validationProps={{ formId: formId, fieldId: "hostName" }}
                      value={agents[selectedAgentIndex].hostName}
                      sx={{ mb: 1 }}
                      onChange={(e) =>
                        setAgents(
                          produce((draft) => {
                            const currentAgent = draft[selectedAgentIndex];
                            if (currentAgent) currentAgent.hostName = e.target.value;
                          })
                        )
                      }
                    />
                  </Grid>
                </Grid>
              </FormSection>
            )}
            {agents[selectedAgentIndex] && !agents[selectedAgentIndex].deleted && (
              <FormSection
                size="small"
                expanded={otherOpen}
                onExpand={() => setOtherOpen((x) => !x)}
                title={t("form:section.title.otherInfos")}
              >
                <Grid spacing={1} container>
                  <Grid position={"relative"} item md={6} sm={12} xs={12}>
                    {errorsMap[selectedAgentIndex] && errorsMap[selectedAgentIndex]["type"] && (
                      <Tooltip title={errorsMap[selectedAgentIndex]["type"][0]}>
                        <IconButton color="error" sx={{ position: "absolute", right: "0px", zIndex: 1000, top: "0px" }}>
                          <ErrorIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <FormAutocomplete
                      label={t("common:type")}
                      options={Object.keys(AgentType)}
                      onChange={(v) => {
                        setAgents(
                          produce((draft) => {
                            const currentAgent = draft[selectedAgentIndex];
                            if (currentAgent) currentAgent.type = v;
                          })
                        );
                      }}
                      validationProps={{ formId: formId, fieldId: "agentType", required: true }}
                      value={agents[selectedAgentIndex].type}
                    />
                    {errorsMap[selectedAgentIndex] && errorsMap[selectedAgentIndex]["dispatchMode"] && (
                      <Tooltip title={errorsMap[selectedAgentIndex]["dispatchMode"][0]}>
                        <IconButton
                          color="error"
                          sx={{ position: "absolute", right: "0px", zIndex: 1000, marginTop: 0.5 }}
                        >
                          <ErrorIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <FormAutocomplete
                      stackProps={{ mt: 1.7 }}
                      label={t("field:label.dispatchMode")}
                      options={Object.keys(AgentDispatchMode)}
                      onChange={(v) => {
                        setAgents(
                          produce((draft) => {
                            const currentAgent = draft[selectedAgentIndex];
                            if (currentAgent) currentAgent.dispatchMode = v;
                          })
                        );
                      }}
                      validationProps={{ formId: formId, fieldId: "dispatchMode", required: true }}
                      value={agents[selectedAgentIndex].dispatchMode}
                    />
                    <FormControlLabel
                      sx={{ mt: 1.5, ml: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={agents[selectedAgentIndex].k8s ?? false}
                          onChange={(v) => {
                            setAgents(
                              produce((draft) => {
                                const currentAgent = draft[selectedAgentIndex];
                                if (currentAgent) currentAgent.k8s = !currentAgent.k8s;
                              })
                            );
                          }}
                        />
                      }
                      label="k8s"
                    />
                  </Grid>
                  <Grid position={"relative"} item md={6} sm={12} xs={12}>
                    {errorsMap[selectedAgentIndex] && errorsMap[selectedAgentIndex]["status"] && (
                      <Tooltip title={errorsMap[selectedAgentIndex]["status"][0]}>
                        <IconButton color="error" sx={{ position: "absolute", right: "0px", zIndex: 1000, top: "0px" }}>
                          <ErrorIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <FormAutocomplete
                      label={t("common:status")}
                      options={Object.keys(AgentStatus)}
                      onChange={(v) => {
                        setAgents(
                          produce((draft) => {
                            const currentAgent = draft[selectedAgentIndex];
                            if (currentAgent) currentAgent.status = v;
                          })
                        );
                      }}
                      validationProps={{ formId: formId, fieldId: "agentStatus", required: true }}
                      value={agents[selectedAgentIndex].status}
                    />
                    <Stack mt={1.5} spacing={0.5} direction={"column"}>
                      {errorsMap[selectedAgentIndex] && errorsMap[selectedAgentIndex]["dispatchPriority"] && (
                        <Tooltip title={errorsMap[selectedAgentIndex]["dispatchPriority"][0]}>
                          <IconButton
                            color="error"
                            sx={{ position: "absolute", right: "0px", zIndex: 1000, marginBottom: 1 }}
                          >
                            <ErrorIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Typography variant="body1">{t("common:dispatchPriority")}</Typography>
                      <FormInputNumber
                        label={t("common:dispatchPriority")}
                        value={agents[selectedAgentIndex].dispatchPriority}
                        stackProps={{ height: "40px", marginTop: "8px" }}
                        validationProps={{ formId: formId, fieldId: `dispatchPriority` }}
                        onChange={(e) =>
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (currentAgent) currentAgent.dispatchPriority = parseInt(e.target.value);
                            })
                          )
                        }
                      />
                    </Stack>
                    <FormControlLabel
                      sx={{ mt: 2.5, ml: 0 }}
                      control={
                        <Switch
                          size="small"
                          checked={agents[selectedAgentIndex].isShared ?? false}
                          onChange={(v) => {
                            setAgents(
                              produce((draft) => {
                                const currentAgent = draft[selectedAgentIndex];
                                if (currentAgent) currentAgent.isShared = !currentAgent.isShared;
                              })
                            );
                          }}
                        />
                      }
                      label={t("field:label.isShared")}
                    />
                  </Grid>
                </Grid>
              </FormSection>
            )}
            {agents[selectedAgentIndex] && !agents[selectedAgentIndex].deleted && (
              <FormSection
                expanded={grpsOpen}
                onExpand={() => setGrpsOpen((x) => !x)}
                title={t("form:section.title.grpsSettings")}
                size="small"
              >
                <Grid spacing={1} container>
                  <Grid item md={6} sm={12} xs={12}>
                    <Stack spacing={0.5} direction={"column"}>
                      <Typography variant="body1">{t("field:label.callsCount")}</Typography>
                      <FormInputNumber
                        label={t("field:label.callsCount")}
                        value={agents[selectedAgentIndex].grpcSettings?.callsCount}
                        stackProps={{ id: `agent-calls-count-label`, height: "40px", marginTop: "8px" }}
                        validationProps={{ formId: formId, fieldId: `agent-calls-count` }}
                        onChange={(e) =>
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (currentAgent && currentAgent.grpcSettings)
                                currentAgent.grpcSettings.callsCount = parseInt(e.target.value);
                            })
                          )
                        }
                      />
                    </Stack>
                    <Stack mt={2.5} spacing={0.5} direction={"column"}>
                      <Typography variant="body1">{t("common:port")}</Typography>
                      <FormInputNumber
                        label={t("common:port")}
                        value={agents[selectedAgentIndex].grpcSettings?.port}
                        stackProps={{ height: "40px", marginTop: "8px" }}
                        validationProps={{ formId: formId, fieldId: `agent-port` }}
                        onChange={(e) =>
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (currentAgent && currentAgent.grpcSettings)
                                currentAgent.grpcSettings.port = parseInt(e.target.value);
                            })
                          )
                        }
                      />
                    </Stack>
                    <Stack mt={2.5} spacing={1} direction={"column"}>
                      <Typography variant="body1">{t("common:user")}</Typography>
                      <FormInputText
                        validationProps={{ formId: formId, fieldId: "grpcs-user" }}
                        value={agents[selectedAgentIndex].grpcSettings?.user}
                        sx={{ mb: 1 }}
                        onChange={(e) =>
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (currentAgent && currentAgent.grpcSettings)
                                currentAgent.grpcSettings.user = e.target.value;
                            })
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid item md={6} sm={12} xs={12}>
                    <Stack spacing={0.5} direction={"column"}>
                      <Typography variant="body1">{t("field:label.updateDelay")}</Typography>
                      <FormInputNumber
                        label={t("field:label.updateDelay")}
                        value={agents[selectedAgentIndex].grpcSettings?.infoUpdateDelayMs}
                        stackProps={{ height: "40px", marginTop: "8px" }}
                        validationProps={{ formId: formId, fieldId: `agent-update-delay` }}
                        onChange={(e) =>
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (currentAgent && currentAgent.grpcSettings)
                                currentAgent.grpcSettings.infoUpdateDelayMs = parseInt(e.target.value);
                            })
                          )
                        }
                      />
                    </Stack>
                    <Stack mt={2.5} spacing={0.5} direction={"column"}>
                      <Typography variant="body1">{t("field:label.timeout")}</Typography>
                      <FormInputNumber
                        label={t("field:label.timeout")}
                        value={agents[selectedAgentIndex].grpcSettings?.timeoutSeconds}
                        stackProps={{ height: "40px", marginTop: "8px" }}
                        validationProps={{ formId: formId, fieldId: `agent-calls-timeout` }}
                        onChange={(e) =>
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (currentAgent && currentAgent.grpcSettings)
                                currentAgent.grpcSettings.timeoutSeconds = parseInt(e.target.value);
                            })
                          )
                        }
                      />
                    </Stack>
                    <Stack mt={2.5} spacing={1} direction={"column"}>
                      <Typography variant="body1">{t("common:password")}</Typography>
                      <FormInputText
                        validationProps={{ formId: formId, fieldId: "grpcs-password" }}
                        value={agents[selectedAgentIndex].grpcSettings?.password}
                        sx={{ mb: 1 }}
                        onChange={(e) =>
                          setAgents(
                            produce((draft) => {
                              const currentAgent = draft[selectedAgentIndex];
                              if (currentAgent && currentAgent.grpcSettings)
                                currentAgent.grpcSettings.password = e.target.value;
                            })
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </FormSection>
            )}
            {agents[selectedAgentIndex] &&
              !agents[selectedAgentIndex].deleted &&
              agents[selectedAgentIndex].licenseSlots &&
              agents[selectedAgentIndex].licenseSlots![selectedSlotIndex] && (
                <SlotCard
                  setAgents={setAgents}
                  selectedAgentIndex={selectedAgentIndex}
                  license={agents[selectedAgentIndex].licenseSlots![selectedSlotIndex]}
                  licenseIndex={selectedSlotIndex}
                  platform={platforms.find(
                    (p) => p.id === agents[selectedAgentIndex].licenseSlots![selectedSlotIndex].platformId
                  )}
                />
              )}
          </Stack>
        </Stack>
      </Stack>
    </>
  );
}

export default AdminSettingsAgentsAndLicenses;

type SlotCardProps = {
  license: DbAgentLicenseResourceInfos;
  licenseIndex: number;
  platform: PlatformInfos | undefined;
  setAgents: any;
  selectedAgentIndex: number;
};

const SlotCard: React.FC<SlotCardProps> = ({ license, platform, setAgents, selectedAgentIndex, licenseIndex }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Stack
      border={`1px solid ${theme.palette.background[200]}`}
      borderRadius={1}
      width={"100%"}
      minWidth={"350px"}
      display={"flex"}
      direction={"column"}
      pl={1}
      pr={1}
      mt={0.5}
      pb={2}
    >
      <Stack
        height={"50px"}
        alignContent={"center"}
        alignItems={"center"}
        direction={"row"}
        justifyContent={"space-between"}
      >
        <Stack alignContent={"center"} alignItems={"center"} height={"50px"} display={"flex"} flexDirection={"row"}>
          <Tooltip title={t("tooltip:title.removeSlot")}>
            <IconButton
              onClick={() => {
                setAgents(
                  produce((draft: DbAgentInfos[]) => {
                    const currentAgent = draft[selectedAgentIndex];
                    if (!currentAgent || !currentAgent.licenseSlots) return;

                    currentAgent.licenseSlots.splice(licenseIndex, 1);
                  })
                );
              }}
            >
              <CloseIcon color="error" />
            </IconButton>
          </Tooltip>
          <Typography color={platform?.name ? "inherit" : "text.tier"} variant="h6">
            {platform?.name ?? t("page:body.unknownPlatform")}
          </Typography>
        </Stack>
        <Button
          onClick={() => {
            setAgents(
              produce((draft: DbAgentInfos[]) => {
                const selectedAgent = draft[selectedAgentIndex];
                if (!selectedAgent || !selectedAgent.licenseSlots) return;
                const selectedSlot = selectedAgent.licenseSlots[licenseIndex];
                if (!selectedSlot) return;

                const newLicenceSlot = {
                  $type:
                    "VDM.AirLab.Sdk.Core.Database.Types.DbAgentLicenseResourceInfos+SlotDefinition, VDM.AirLab.Sdk.Core",

                  jobType: null,
                  slotNumbers: 0,
                };
                selectedSlot.slots = selectedSlot.slots ? [...selectedSlot.slots, newLicenceSlot] : [newLicenceSlot];
              })
            );
          }}
          sx={{ height: "35px" }}
          endIcon={<AddIcon />}
        >
          {t("button:newSlot")}
        </Button>
      </Stack>
      <Stack spacing={1} direction={"column"}>
        {license.slots?.map((x, i) => (
          <Stack
            justifyContent={"space-between"}
            spacing={1}
            pb={1}
            pl={1}
            alignContent={"center"}
            alignItems={"center"}
            direction={"row"}
            sx={{ background: theme.palette.background.paper }}
          >
            <Stack alignContent={"center"} alignItems={"center"} direction={"row"} spacing={1}>
              <Typography sx={{ marginTop: "8px !important" }} color={"text.tier"} variant="body2">
                {t("page:body.jobType")}
              </Typography>
              <FormAutocomplete
                sx={{ width: "350px", mt: -2, maxWidth: "15vw" }}
                value={x?.jobType}
                onChange={(v) => {
                  setAgents(
                    produce((draft: DbAgentInfos[]) => {
                      const currentAgent = draft[selectedAgentIndex];
                      if (!currentAgent.licenseSlots) return;
                      const currentLicense = currentAgent.licenseSlots[licenseIndex];
                      if (!currentLicense) return;
                      currentLicense.slots![i].jobType = v;
                    })
                  );
                }}
                options={Object.keys(JobTypes)}
                validationProps={{ formId: formId, fieldId: `agent-job-type-${i}` }}
              />
            </Stack>
            <Stack alignContent={"center"} alignItems={"center"} direction={"row"} spacing={1}>
              <Typography sx={{ marginTop: "8px !important" }} color={"text.tier"} variant="body2">
                {t("page:body.slotNumbers")}
              </Typography>
              <FormInputNumber
                value={x?.slotNumbers}
                sx={{ marginTop: "8px", width: "64px" }}
                stackProps={{ height: "48px" }}
                validationProps={{ formId: formId, fieldId: `agent-slot-number-${i}` }}
                onChange={(e) => {
                  setAgents(
                    produce((draft: DbAgentInfos[]) => {
                      const currentAgent = draft[selectedAgentIndex];
                      if (!currentAgent.licenseSlots) return;
                      const currentLicense = currentAgent.licenseSlots[licenseIndex];
                      if (!currentLicense) return;

                      currentLicense.slots![i].slotNumbers = parseInt(e.target.value);
                    })
                  );
                }}
              />
            </Stack>
            <Tooltip title={t("tooltip:title.removeSlot")}>
              <IconButton
                onClick={() => {
                  setAgents(
                    produce((draft: DbAgentInfos[]) => {
                      const selectedAgent = draft[selectedAgentIndex];
                      if (!selectedAgent || !selectedAgent.licenseSlots) return;
                      const selectedSlot = selectedAgent.licenseSlots[licenseIndex];
                      if (!selectedSlot) return;

                      selectedSlot.slots?.splice(i, 1);
                    })
                  );
                }}
                sx={{ width: "35px", height: "35px", marginTop: "8px !important" }}
              >
                <CloseIcon color="error" />
              </IconButton>
            </Tooltip>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
};
